import type { BarPackage, BudgetTier, CalculatorInputs, Intensity } from '@/lib/calculator'
import { BUDGET_PRICES } from '@/lib/calculator'

interface Props {
  inputs: CalculatorInputs
  onChange: <K extends keyof CalculatorInputs>(key: K, value: CalculatorInputs[K]) => void
}

const INTENSITY_OPTIONS: { value: Intensity, label: string, sub: string }[] = [
  { value: 'light', label: 'Light', sub: '1 drink/person/hr' },
  { value: 'average', label: 'Average', sub: '1.25 drinks/person/hr' },
  { value: 'heavy', label: 'Party / Heavy', sub: '1.5-2 drinks/person/hr' },
]

const BAR_PACKAGE_OPTIONS: { value: BarPackage, label: string, sub: string }[] = [
  { value: 'open', label: 'Full Open Bar', sub: '50% Wine · 30% Beer · 20% Spirits' },
  { value: 'beerWine', label: 'Beer & Wine Only', sub: '60% Wine · 40% Beer' },
  { value: 'cocktails', label: 'Beer, Wine & Cocktails', sub: '40% Wine · 30% Beer · 30% Cocktails' },
  { value: 'custom', label: 'Custom Breakdown', sub: 'Set your own ratios' },
]

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block font-serif text-lg font-semibold text-[#222222] mb-2">
      {children}
    </label>
  )
}

export default function CalculatorInputsPanel({ inputs, onChange }: Props) {
  const customTotal = inputs.customWinePct + inputs.customBeerPct + inputs.customSpiritsPct

  return (
    <div className="space-y-8">
      {/* Guest Count */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Guest Count</SectionLabel>
          <input
            type="number"
            min={1}
            max={999}
            value={inputs.guestCount}
            onChange={e => onChange('guestCount', Math.max(1, Number(e.target.value) || 1))}
            aria-label="Guest count number input"
            className="w-24 text-right font-semibold text-[#6B1D2F] bg-[#F7EBE8] rounded-lg px-3 py-1.5 border border-[#D4AF37]/30 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          />
        </div>
        <input
          type="range"
          min={1}
          max={500}
          value={Math.min(inputs.guestCount, 500)}
          onChange={e => onChange('guestCount', Number(e.target.value))}
          aria-label="Guest count slider"
          className="w-full accent-[#6B1D2F]"
        />
      </div>

      {/* Duration */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Reception Duration</SectionLabel>
          <span className="font-semibold text-[#6B1D2F]">{inputs.hours} hrs</span>
        </div>
        <input
          type="range"
          min={1}
          max={8}
          value={inputs.hours}
          onChange={e => onChange('hours', Number(e.target.value))}
          aria-label="Reception duration in hours"
          className="w-full accent-[#6B1D2F]"
        />
      </div>

      {/* Drinking % */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Drinking Guests</SectionLabel>
          <span className="font-semibold text-[#6B1D2F]">{inputs.drinkingPercent}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={inputs.drinkingPercent}
          onChange={e => onChange('drinkingPercent', Number(e.target.value))}
          aria-label="Percentage of guests who drink alcohol"
          className="w-full accent-[#6B1D2F]"
        />
      </div>

      {/* Intensity */}
      <div>
        <SectionLabel>Drinker Intensity Profile</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {INTENSITY_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange('intensity', opt.value)}
              aria-pressed={inputs.intensity === opt.value}
              className={`rounded-xl border-2 p-3 text-left transition-all ${
                inputs.intensity === opt.value
                  ? 'border-[#6B1D2F] bg-[#F7EBE8] shadow-md'
                  : 'border-gray-200 bg-white hover:border-[#D4AF37]'
              }`}
            >
              <div className="font-semibold text-[#222222] text-sm">{opt.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{opt.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Bar Package */}
      <div>
        <SectionLabel>Bar Package Type</SectionLabel>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {BAR_PACKAGE_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange('barPackage', opt.value)}
              aria-pressed={inputs.barPackage === opt.value}
              className={`rounded-xl border-2 p-3 text-left transition-all ${
                inputs.barPackage === opt.value
                  ? 'border-[#6B1D2F] bg-[#F7EBE8] shadow-md'
                  : 'border-gray-200 bg-white hover:border-[#D4AF37]'
              }`}
            >
              <div className="font-semibold text-[#222222] text-sm">{opt.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{opt.sub}</div>
            </button>
          ))}
        </div>

        {inputs.barPackage === 'custom' && (
          <div className={`mt-4 rounded-xl p-4 bg-[#FAF8F5] border border-[#D4AF37]/30 space-y-4 ${customTotal !== 100 ? 'ring-1 ring-amber-400' : ''}`}>
            {([
              ['customWinePct', 'Wine %'],
              ['customBeerPct', 'Beer %'],
              ['customSpiritsPct', 'Spirits %'],
            ] as [keyof CalculatorInputs, string][]).map(([key, label]) => (
              <div key={key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-[#222222] font-medium">{label}</span>
                  <span className="text-[#6B1D2F] font-semibold">{inputs[key] as number}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={inputs[key] as number}
                  onChange={e => onChange(key, Number(e.target.value) as never)}
                  aria-label={`${label} slider`}
                  className="w-full accent-[#6B1D2F]"
                />
              </div>
            ))}
            <p className={`text-xs ${customTotal === 100 ? 'text-green-600' : 'text-amber-600'}`}>
              Total: {customTotal}% {customTotal !== 100 && '(will auto-balance to 100%)'}
            </p>
          </div>
        )}
      </div>

      {/* Champagne toggle */}
      <div className="flex items-center justify-between rounded-xl bg-[#F7EBE8] p-4">
        <div>
          <div className="font-semibold text-[#222222]">Champagne Toast</div>
          <div className="text-xs text-gray-500">1/6 bottle per guest for a 4oz toast</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={inputs.champagneToast}
          aria-label="Toggle champagne toast"
          onClick={() => onChange('champagneToast', !inputs.champagneToast)}
          className={`relative w-14 h-8 rounded-full transition-colors ${inputs.champagneToast ? 'bg-[#6B1D2F]' : 'bg-gray-300'}`}
        >
          <span className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${inputs.champagneToast ? 'translate-x-6' : ''}`} />
        </button>
      </div>

      {/* Season + hot outdoor */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <SectionLabel>Season</SectionLabel>
          <select
            value={inputs.season}
            onChange={e => onChange('season', e.target.value as CalculatorInputs['season'])}
            aria-label="Wedding season"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
          >
            <option value="summer">Summer (more white/rosé)</option>
            <option value="winter">Winter (more red)</option>
          </select>
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-[#222222] cursor-pointer">
            <input
              type="checkbox"
              checked={inputs.hotOutdoor}
              onChange={e => onChange('hotOutdoor', e.target.checked)}
              aria-label="Hot outdoor wedding, increases ice needed"
              className="w-4 h-4 accent-[#6B1D2F]"
            />
            Hot outdoor wedding (+ice)
          </label>
        </div>
      </div>

      {/* Budget tier */}
      <div>
        <SectionLabel>Budget Class</SectionLabel>
        <select
          value={inputs.budgetTier}
          onChange={e => onChange('budgetTier', e.target.value as BudgetTier)}
          aria-label="Budget tier for pricing estimate"
          className="w-full rounded-lg border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
        >
          {Object.entries(BUDGET_PRICES).map(([key, val]) => (
            <option key={key} value={key}>{val.label}</option>
          ))}
        </select>
      </div>
    </div>
  )
}
